import { useCallback, useEffect, useRef, useState } from 'react';

import { useCreateSession } from '../../../queries';
import type { SessionDetailsResponse } from '../../../domain';
import { useRequireSignIn } from '../../../components/auth/useRequireSignIn';
import { SignInRequiredError } from '../../../components/auth/signInRequiredError';

import { buildSessionCreateRequest } from './sessionHelpers';

interface UseImageLabSessionResult {
  sessionId: string | undefined;
  ensureSession: () => Promise<string>;
  isCreatingSession: boolean;
  sessionError: Error | null;
}

/**
 * Studio-bound session lifecycle. Lazily creates a session on the first
 * `ensureSession()` call; subsequent calls return the same id without
 * hitting the BE. Concurrent calls (e.g. user double-clicks submit while
 * the first create is still in flight) coalesce onto a single in-flight
 * promise via `pendingRef`. Switching studios resets the session — each
 * studio gets its own lifecycle.
 */
export function useImageLabSession(studioId: string | undefined): UseImageLabSessionResult {
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [sessionError, setSessionError] = useState<Error | null>(null);
  const pendingRef = useRef<Promise<string> | null>(null);
  const createMutation = useCreateSession();
  const { requireSignIn } = useRequireSignIn();

  // Reset whenever the studio changes — a Fashion session shouldn't carry
  // over into the Product Photography studio's operation surface.
  useEffect(() => {
    setSessionId(undefined);
    setSessionError(null);
    pendingRef.current = null;
  }, [studioId]);

  const ensureSession = useCallback(async (): Promise<string> => {
    // A session is the first write every operation and upload makes, so the
    // sign-in gate sits here: a visitor is prompted before anything is sent.
    if (!requireSignIn()) throw new SignInRequiredError();
    if (sessionId) return sessionId;
    if (pendingRef.current) return pendingRef.current;
    const request = buildSessionCreateRequest(studioId);
    const promise = createMutation
      .mutateAsync(request)
      .then((res: SessionDetailsResponse) => {
        setSessionId(res.sessionId);
        setSessionError(null);
        pendingRef.current = null;
        return res.sessionId;
      })
      .catch((err: unknown) => {
        const wrapped = err instanceof Error ? err : new Error(String(err));
        setSessionError(wrapped);
        pendingRef.current = null;
        throw wrapped;
      });
    pendingRef.current = promise;
    return promise;
  }, [sessionId, studioId, createMutation, requireSignIn]);

  return {
    sessionId,
    ensureSession,
    isCreatingSession: createMutation.isPending,
    sessionError,
  };
}
